import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router';
import { Button } from '@mendeley-internal/react-ui';

import LinkButton from './LinkButton';
import Icon from './Icon/Icon';
import Notification from './Notification';
import { saveToLibrary } from '../actions/library';
import { signInToAddToLibrary, signInToViewPdf } from '../actions/session';
import { createUrl, getQueryObject, getQueryString } from '../utils/url';
import OldButton from './Button/Button';

const SubmitButton = ({ pending, className }) => (
  <Button
    size="medium"
    iconName={pending ? 'loading' : 'plus'}
    animation={pending ? 'spin' : null}
    iconBefore
    disabled={pending}
    data-name="save-reference"
    className={className}
  >
    Add to library
  </Button>
);

const OldSubmitButton = ({ pending, track }) => (
  <OldButton
    type="submit"
    disabled={pending}
    data-name="save-reference"
    withIcon
    buttonStyle="basic"
    track={track}
  >
    <Icon name={pending ? 'loading' : 'plus'} spin={pending} />
    <span>Add to library</span>
  </OldButton>
);

const ErrorMessage = () => (
  <Notification type="error">
    Failed to add the document to your library.
  </Notification>
);

function LoginButton(props) {
  const {
    hasPdf,
    isEntitled,
    id,
    to,
    track,
  } = props;

  if (hasPdf && isEntitled) {
    return (
      <LinkButton
        to={to}
        track={track}
        external
        data-name="view-pdf"
        withIcon
        buttonStyle="basic"
        onClick={() => props.signInToViewPdf(id)}
      >
        <Icon name="plus" />
        <span>Sign in to view PDF</span>
      </LinkButton>
    );
  }

  return (
    <LinkButton
      to={to}
      track={track}
      external
      data-name="save-reference"
      withIcon
      buttonStyle="basic"
      onClick={() => props.signInToAddToLibrary(id)}
    >
      <Icon name="plus" />
      <span>Sign in to add to library</span>
    </LinkButton>
  );
}

const SavedButton = ({ className }) => (
  <Button
    size="medium"
    iconName="check"
    iconBefore
    disabled
    data-name="saved-reference"
    className={className}
  >
    Reference added
  </Button>
);

const OldSavedButton = ({ className }) => (
  <OldButton
    disabled
    buttonStyle="basic"
    data-name="saved-reference"
    className={`button-success ${className}`}
    withIcon
  >
    <Icon name="check" />
    Reference added
  </OldButton>
);

const SaveToLibrary = ({
  doc,
  doc: {
    id,
    hasPdf,
  },
  save,
  library,
  isAuthenticated,
  baseURL,
  location,
  track,
  tertiary,
  currentPage,
  position,
  isEntitled,
  isSuggested,
  ...props
}) => {
  const { exists, error, pending } = library[id] || {};
  const newQuery = {
    ...getQueryObject(location.search),
  };

  if (!isEntitled) {
    newQuery.save = id;
  }

  const routeTo = {
    ...location,
    search: `?${getQueryString(newQuery)}`
  };
  const loginPath = `/sign/in/?routeTo=${encodeURIComponent(createUrl(baseURL, routeTo))}`;

  let component = null;

  if (error && error === 401) return <Redirect to={loginPath} />;

  if (exists) {
    component = tertiary
      ? <OldSavedButton {...props} />
      : <SavedButton {...props} />;
  }

  const trackDetails = track
    ? {
      ...track,
      action: 'AddToLibrary',
      value: { documentId: id },
      version: 2,
    }
    : {};

  if (isAuthenticated && !component) {
    const button = tertiary
      ? <OldSubmitButton pending={pending} track={trackDetails} {...props} />
      : <SubmitButton pending={pending} track={trackDetails} {...props} />;

    component = (
      <form
        data-name="form"
        onSubmit={(e) => {
          e.preventDefault();
          save({
            doc,
            position,
            currentPage,
            isSuggested,
          });
        }}
      >
        {button}
        { error && <ErrorMessage /> }
      </form>
    );
  }

  if (!isAuthenticated && !component) {
    component = (
      <LoginButton
        id={id}
        hasPdf={hasPdf}
        to={loginPath}
        track={trackDetails}
        isEntitled={isEntitled}
        signInToViewPdf={props.signInToViewPdf}
        signInToAddToLibrary={props.signInToAddToLibrary}
      />
    );
  }

  return component;
};

SaveToLibrary.propTypes = {
  track: PropTypes.object.isRequired,
  doc: PropTypes.object,
};

const mapStateToProps = ({
  library,
  session,
  location: { baseURL }
}) => ({
  library,
  isAuthenticated: session.isAuthenticated,
  baseURL
});

const mapDispatchToProps = {
  save: saveToLibrary,
  signInToAddToLibrary,
  signInToViewPdf,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SaveToLibrary));



// WEBPACK FOOTER //
// ./src/universal/ui/components/SaveToLibrary.js